import React, { useEffect, useRef, useState } from 'react';
import { Upload, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cx } from '@/lib/format';

const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';

export function ImgPreview({ url, ratio = 'aspect-video', label }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [url]);
  return (
    <div className={cx('relative w-full overflow-hidden rounded-lg border border-border bg-[hsl(var(--gaze-ivory))]', ratio)}>
      {url && !broken ? (
        <img src={url} alt={label || 'preview'} className="h-full w-full object-cover" onError={() => setBroken(true)} />
      ) : (
        <div className="grid h-full w-full place-items-center text-muted-foreground">
          <div className="flex flex-col items-center gap-1 text-[11px]"><ImageOff size={18} />{broken ? 'Invalid image URL' : 'No image'}</div>
        </div>
      )}
    </div>
  );
}

// ---- Chunked image upload (bypasses proxy body-size limits) ----
const CHUNK_SIZE = 512 * 1024; // 512 KB
const newUploadId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export async function uploadImageChunked(file, onProgress) {
  const uploadId = newUploadId();
  const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  let last = null;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(i));
    fd.append('total_chunks', String(total));
    fd.append('filename', file.name);
    fd.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
    const r = await api.post('/admin/uploads/chunk', fd);
    last = r.data;
    if (onProgress) onProgress(Math.round(((i + 1) / total) * 100));
  }
  return last?.url;
}

// URL input + "Upload" button + optional live preview for one image slot
export function ImageField({ label, value, onChange, ratio = 'aspect-video', testid, previewClass = '', showPreview = true }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10 MB'); return; }
    setBusy(true); setProg(0);
    try {
      const url = await uploadImageChunked(file, setProg);
      if (url) { onChange(url); toast.success('Image uploaded'); }
      else toast.error('Upload failed — please try again');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed — please try again');
    } finally { setBusy(false); }
  };

  return (
    <div>
      {label && <label className="text-xs font-medium text-muted-foreground">{label}</label>}
      <div className="flex gap-2">
        <input value={value} onChange={(e) => onChange(e.target.value)} className={field} placeholder="https://... or upload" data-testid={testid} disabled={busy} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={testid ? `${testid}-upload` : undefined}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-[hsl(var(--gaze-ivory))] disabled:opacity-60">
          <Upload size={14} /> {busy ? `${prog}%` : 'Upload'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={testid ? `${testid}-file` : undefined} />
      </div>
      {busy && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[hsl(var(--gaze-ivory))]">
          <div className="h-full rounded-full bg-[hsl(var(--gaze-gold))] transition-all" style={{ width: `${prog}%` }} />
        </div>
      )}
      {showPreview && <div className={cx('mt-2', previewClass)}><ImgPreview url={value} ratio={ratio} label={label} /></div>}
    </div>
  );
}
